import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import ProfileModal from '../ProfileModal';
import { API_ENDPOINTS } from '../../utils/config';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const userMenuRef = useRef(null);
  const [userRole, setUserRole] = useState(null);

  // Fetch user's role if not in user object
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user?.id) return;
      
      // First check if role is already in user object
      const roleFromUser = user?.role_detail?.name || 
                          user?.role?.name || 
                          user?.role_name || 
                          user?.role;
      
      if (roleFromUser) {
        setUserRole(roleFromUser);
        return;
      }

      // If not found, try to fetch from API
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Accept': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const response = await axios.get(API_ENDPOINTS.USER_ROLES_LIST, { headers });
        const userRolesData = Array.isArray(response.data)
          ? response.data
          : (response.data?.results || response.data?.data || response.data?.user_roles || []);

        // Find current user's role
        const currentUserRole = userRolesData.find(ur => 
          ur?.user_detail?.id === user.id || 
          ur?.user_detail?.username === user.username ||
          ur?.user?.id === user.id
        );

        if (currentUserRole) {
          const roleName = currentUserRole?.role_detail?.name || 
                          currentUserRole?.role?.name || 
                          currentUserRole?.role_name ||
                          currentUserRole?.role;
          setUserRole(roleName);
        }
      } catch (error) {
        // Silently handle error - role might not be available
      }
    };

    fetchUserRole();
  }, [user]);

  // Check if user is admin
  const isAdmin = useMemo(() => {
    if (!user) return false;
    
    // Try multiple possible role field paths from user object
    const roleName = user?.role_detail?.name || 
                     user?.role_detail?.role_name ||
                     user?.role?.name || 
                     user?.role?.role_name ||
                     user?.role_name || 
                     user?.role ||
                     user?.user_role?.role_detail?.name ||
                     user?.user_role?.role?.name ||
                     user?.user_role?.role_name ||
                     userRole || // Use fetched role
                     '';
    
    // Convert to string and check (case-insensitive)
    const normalizedRole = String(roleName).toLowerCase().trim();
    
    // Check if role name contains "admin" or matches common admin role names
    const adminRoleNames = ['admin', 'administrator', 'super admin', 'superadmin', 'system admin', 'systemadmin'];
    return adminRoleNames.some(adminRole => 
      normalizedRole === adminRole || 
      normalizedRole.includes(adminRole) ||
      normalizedRole.startsWith(adminRole) ||
      normalizedRole.endsWith(adminRole)
    );
  }, [user, userRole]);

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'Home',
      tooltip: 'Overview and activity monitoring'
    },
    {
      label: 'Role Management',
      path: '/role-management',
      icon: 'Shield',
      tooltip: 'Manage roles and user assignments'
    },    
    {
      label: 'Clients',
      path: '/client-management',
      icon: 'Users',
      tooltip: 'Customer relationship management'
    },
    {
      label: 'Properties',
      path: '/property-management',
      icon: 'Building',
      tooltip: 'Manage property portfolio and details'
    },
    {
      label: 'Surveys',
      path: '/surveys',
      icon: 'ClipboardList',
      tooltip: 'View and manage surveys'
    },
    {
      label: 'Reports',
      path: '/reports-analytics',
      icon: 'BarChart3',
      tooltip: 'Business intelligence and report generation'
    },
    {
      label: 'Finance',
      path: '/finance-tracker',
      icon: 'DollarSign',
      tooltip: 'Financial tracking and management'
    }
  ];

  const normalizeAllowed = useMemo(() => {
    const raw =
      user?.allowedModules ||
      user?.modules ||
      user?.access_modules ||
      user?.module_access ||
      user?.permissions ||
      [];

    if (!raw || (Array.isArray(raw) && raw.length === 0)) return null;

    const asArray = Array.isArray(raw) ? raw : Object.values(raw || {});
    const normalized = asArray
      .map((entry) => {
        if (typeof entry === 'string') return entry.toLowerCase();
        if (entry?.path) return String(entry.path).toLowerCase();
        if (entry?.label) return String(entry.label).toLowerCase();
        if (entry?.name) return String(entry.name).toLowerCase();
        return null;
      })
      .filter(Boolean);

    return normalized.length ? normalized : null;
  }, [user]);

  const isModuleAllowed = (item) => {
    if (!normalizeAllowed) return true; // no restriction configured
    const label = item?.label?.toLowerCase();
    const path = item?.path?.toLowerCase();
    return normalizeAllowed.some((allowed) => {
      if (!allowed) return false;
      return (
        (label && allowed.includes(label)) ||
        (path && allowed === path) ||
        (path && allowed.includes(path))
      );
    });
  };

  const filteredNavigation = useMemo(
    () => navigationItems.filter((item) => isModuleAllowed(item)),
    [navigationItems, normalizeAllowed]
  );

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsUserMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-[1000] bg-card border-b border-border shadow-elevation-1">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Logo */}
        <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
          <div className="flex items-center space-x-3">
            <img 
              src="/assets/icon/valfin_logo.png" 
              alt="ValfinSurvey Pro" 
              className="w-14 h-14 object-contain"
            />
            <div className="flex flex-col">
              <span className="text-lg font-semibold text-foreground leading-none">
                ValfinSurvey
              </span>
              <span className="text-xs text-muted-foreground font-medium leading-none mt-0.5">
                Pro
              </span>
            </div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          {filteredNavigation?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-smooth
                ${isActivePath(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
              title={item?.tooltip}
            >
              <Icon name={item?.icon} size={18} />
              <span>{item?.label}</span>
            </button>
          ))}
        </nav>

        {/* User Menu */}
        <div className="flex items-center space-x-3">
          {/* User Profile */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {user?.username?.charAt(0) || 'U'}
                </span>
              </div>
              <span className="hidden sm:block">{user?.username || 'User'}</span>
              <Icon name="ChevronDown" size={16} />
            </button>

            {/* User Dropdown Menu */}
            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{user?.username}</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => {
                      setIsProfileModalOpen(true);
                      setIsUserMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    Profile
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
              className="h-10 w-10"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} />
            </Button>
          </div>
        </div>
      </div>
      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-card border-t border-border shadow-elevation-2">
          <nav className="px-4 py-2 space-y-1">
          {filteredNavigation?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`
                  flex items-center space-x-3 w-full px-3 py-3 rounded-md text-sm font-medium transition-smooth
                  ${isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name={item?.icon} size={20} />
                <span>{item?.label}</span>
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Profile Modal */}
      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        user={user}
      />
    </header>
  );
};

export default Header;